import { ColumnSort } from "@tanstack/react-table";
import {
  getEndpointPartnerEventCodes,
  useDebounceState,
} from "@theprelude/core";
import { ControlCategories } from "@theprelude/sdk";
import { useMemo, useState } from "react";
import {
  AdditionalFilter,
  anyCloudRegionFilter,
  anyDeviceTypeFilter,
  anyDurationLastSeenFilter,
  anyLastSeenFilter,
  anyOwnershipFilter,
  anyPlatformFilter,
  durationLastSeenFilter,
  missingScanFilter,
  noEDRFilter,
  noEndpointManagementFilter,
  noVulnerabilityManagementFilter,
  outOfDateScanFilter,
  rfmFilter,
} from "@/components/filter-form/additional-filters";
import {
  getOdataFilters,
  getEndpointOdataSearchFilters,
} from "@/components/filter-form/odata";
import { ResponsiveContainer } from "@/components/responsive-container";
import {
  sortByField,
  getColumnVisibility,
  endpointHostnameColumn,
  endpointSerialColumn,
  getColumnWithControl,
  policyLastSeenColumn,
} from "@/components/table/columns";
import { TableFilterSelector } from "@/components/table/filter-selector";
import { InfiniteTable } from "@/components/table/infinite-table";
import { useInfiniteEndpoints } from "@/hooks/use-infinite-endpoints";
import { Filter } from "@/lib/filter";
import {
  useExceptedFilters,
  useTopLevelFiltersFor,
} from "@/queries/control-monitor";
import { SCMTableFilterInput } from "../../table/filter-input";
import { useTable } from "../../table/hooks";
import { ExceptedFilterToggle } from "../excepted-filter-toggle";
import { ExportEndpointsIconButton } from "../export-endpoints-button";
import { TopLevelFilterIndicator } from "../top-level-filter-indicator";
import { ResultCount } from "./result-count";
import {
  formatSCMEndpointData,
  getControlInstanceAdditionalFilters,
  SCMEndpointEntry,
  useEndpointControls,
  useSCMEndpointColumns,
} from "./scm-columns";
import { SCMEmptyState } from "./scm-table-empty-state";

const getRowId = (endpoint: SCMEndpointEntry) => endpoint.rowId;

const controlCategories = [
  ControlCategories.XDR,
  ControlCategories.ASSET_MANAGER,
  ControlCategories.VULN_MANAGER,
];

export const AllEndpointsTable = ({
  filters,
  onFilter,
}: {
  filters: Filter[];
  onFilter: (filters: Filter[]) => void;
}) => {
  const [columnSort, setColumnSort] = useState<ColumnSort[]>([]);
  const searchFilter = useDebounceState("");
  const searchValue = searchFilter.debouncedValue.toLowerCase();

  const topLevelFilters = useTopLevelFiltersFor("endpoints");
  const { exceptedFilters } = useExceptedFilters();

  const endpointControls = useEndpointControls();
  const visibleColumns = [
    endpointHostnameColumn.id,
    endpointSerialColumn.id,
    ...endpointControls.map(
      (c) => getColumnWithControl(policyLastSeenColumn, c.id, c.instance_id).id,
    ),
  ];

  const columns = useSCMEndpointColumns({
    endpointControls,
    noControlEventCodes: getEndpointPartnerEventCodes(undefined),
    controlCategories,
    defaultVisibleColumns: visibleColumns,
  });

  const query = useMemo(() => {
    const sortedColumn = columns.find((c) => c.id === columnSort[0]?.id);
    return {
      filter: {
        and: [
          ...getOdataFilters(filters),
          ...getOdataFilters(topLevelFilters),
          ...getOdataFilters(exceptedFilters),
          searchValue && getEndpointOdataSearchFilters(searchValue),
        ],
      },
      orderBy: sortByField(columnSort[0], sortedColumn?.meta?.filter?.odata),
    };
  }, [
    searchValue,
    columnSort,
    exceptedFilters,
    filters,
    topLevelFilters,
    columns,
  ]);

  const { data, fetchNextPage, hasNextPage, isFetching, isLoading } =
    useInfiniteEndpoints({ query });

  const endpoints = useMemo(
    () =>
      formatSCMEndpointData({ endpoints: data?.data ?? [], controlCategories }),
    [data],
  );

  const fetchedCount = endpoints.length;
  const totalCount = data?.totalCount ?? 0;

  const additionalFilters: AdditionalFilter[] = [
    anyCloudRegionFilter,
    anyDeviceTypeFilter,
    anyDurationLastSeenFilter,
    anyLastSeenFilter,
    anyOwnershipFilter,
    anyPlatformFilter,
    missingScanFilter,
    noEDRFilter,
    noEndpointManagementFilter,
    noVulnerabilityManagementFilter,
    outOfDateScanFilter,
    rfmFilter,
    ...getControlInstanceAdditionalFilters(endpointControls, [
      durationLastSeenFilter,
    ]),
  ];

  const table = useTable({
    name: "all-endpoints",
    data: endpoints,
    columns,
    manualSorting: true,
    manualFiltering: true,
    initialState: {
      columnVisibility: getColumnVisibility(columns),
    },
    state: {
      sorting: columnSort,
    },
    onSortingChange: setColumnSort,
    getRowId,
    meta: { additionalFilters },
  });

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex items-center gap-2">
        <SCMTableFilterInput
          value={searchFilter.value}
          onChange={(value) => searchFilter.setDelayed(value)}
          onClear={() => searchFilter.setImmediate("")}
        />
        <TableFilterSelector
          table={table}
          currentFilters={filters}
          setFilters={onFilter}
        />
        <ExportEndpointsIconButton query={query} />
      </div>
      <TopLevelFilterIndicator category="endpoints" />
      <div className="flex items-center justify-between">
        <ResultCount
          loading={isLoading}
          count={fetchedCount}
          totalCount={totalCount}
          resourceType="endpoint"
        />
        <ExceptedFilterToggle />
      </div>
      <ResponsiveContainer className="flex-1 overflow-hidden">
        {({ height }) => (
          <InfiniteTable
            table={table}
            loading={isLoading}
            isFetching={isFetching}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
            height={height}
            emptyState={<SCMEmptyState resourceType="endpoint" />}
          />
        )}
      </ResponsiveContainer>
    </div>
  );
};
